import { useEffect, useMemo, useState, useCallback } from "react";
import { api, brl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FileText, Search, RefreshCw, ArrowDownRight, ArrowUpRight, Filter, X,
  Paperclip, FileCode2, MoreHorizontal, Plus, UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { FornecedorPicker } from "@/components/FornecedorPicker";

const TIPOS = ["NF-e", "NFS-e", "NFC-e", "outro"];
const DIRECOES = [
  { key: "entrada", label: "Entrada" },
  { key: "saida", label: "Saída" },
];
const STATUSES = [
  { key: "regular", label: "Regular", cls: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20" },
  { key: "pendente", label: "Pendente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "divergencia", label: "Divergência", cls: "bg-[#F7E4DE] text-[#D4A652] border-[#D4A652]/30" },
  { key: "nao_aplicavel", label: "N/A", cls: "bg-stone-100 text-stone-500 border-stone-300" },
  { key: "cancelado", label: "Cancelado", cls: "bg-stone-200 text-stone-500 border-stone-400" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function fmtDataBR(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function fmtDataHoraBR(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "—";
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch { return "—"; }
}

function fmtBytes(n) {
  const v = Number(n || 0);
  if (!v || v < 0) return "—";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(2)} MB`;
}

// F8.6 — Regras de anexos fiscais (espelhando o contrato do backend)
const DF_MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB — igual ao DF_MAX_BYTES
const DF_XML_MIME_ALLOWED = ["application/xml", "text/xml"];
const DF_PDF_MIME_ALLOWED = ["application/pdf"];

// F8.7 — Rótulos e estilos de ações de auditoria (usa exatamente os valores do backend)
const DF_ACAO_LABELS = {
  criada: { label: "Criação", cls: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20" },
  atualizada: { label: "Edição", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  status_alterado: { label: "Status alterado", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cancelada: { label: "Cancelamento", cls: "bg-stone-200 text-stone-600 border-stone-400" },
  vinculada: { label: "Vínculo criado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  desvinculada: { label: "Vínculo removido", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  arquivo_adicionado: { label: "Arquivo anexado", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

function labelAcaoDF(acao) {
  return DF_ACAO_LABELS[acao] || { label: acao || "—", cls: "bg-stone-100 text-stone-600 border-stone-300" };
}

function resumoEventoDF(ev) {
  if (!ev) return "";
  const ex = ev.extras || {};
  switch (ev.acao) {
    case "criada":
      return "Documento fiscal criado.";
    case "atualizada": {
      const campos = Array.isArray(ex.campos_alterados) ? ex.campos_alterados
        : (ex.campo ? [ex.campo] : []);
      return campos.length
        ? `Campos alterados: ${campos.join(", ")}`
        : "Documento editado.";
    }
    case "status_alterado":
      return `Status: ${ex.status_antes || "—"} → ${ex.status_depois || "—"}${ex.motivo ? ` · Motivo: ${ex.motivo}` : ""}`;
    case "cancelada":
      return `Documento cancelado${ex.motivo ? ` · Motivo: ${ex.motivo}` : ""}.`;
    case "vinculada":
      return `Vínculo ${ex.campo || "—"}${ex.depois ? ` → ${String(ex.depois).slice(0, 12)}…` : ""}${ex.motivo ? ` · Motivo: ${ex.motivo}` : ""}`;
    case "desvinculada":
      return `Vínculo ${ex.campo || "—"} removido${ex.antes ? ` (era ${String(ex.antes).slice(0, 12)}…)` : ""}${ex.motivo ? ` · Motivo: ${ex.motivo}` : ""}`;
    case "arquivo_adicionado": {
      const tipo = (ex.tipo_arquivo || "").toUpperCase();
      const nome = ex.filename ? ` "${ex.filename}"` : "";
      const sz = ex.size ? ` (${fmtBytes(ex.size)})` : "";
      return `Anexo ${tipo}${nome}${sz}`;
    }
    default:
      return "";
  }
}

function parseFilenameFromDisposition(disposition) {
  if (!disposition) return null;
  // Suporta filename*=UTF-8''... e filename="..."
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    try { return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, "")); }
    catch { /* ignore */ }
  }
  const simple = /filename="?([^";]+)"?/i.exec(disposition);
  return simple?.[1] ? simple[1].trim() : null;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "arquivo";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.regular;
  return (
    <span
      data-testid={`df-status-${status}`}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

function DirecaoTag({ direcao }) {
  const isIn = direcao === "entrada";
  const Icon = isIn ? ArrowDownRight : ArrowUpRight;
  return (
    <span
      data-testid={`df-direcao-${direcao}`}
      className={`inline-flex items-center gap-1.5 text-xs font-medium ${
        isIn ? "text-[#083A6B]" : "text-[#D4A652]"
      }`}
    >
      <Icon size={13} strokeWidth={2} />
      {isIn ? "Entrada" : "Saída"}
    </span>
  );
}

function AnexosIcons({ doc }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-stone-400">
      {doc.xml_ref ? (
        <FileCode2 size={13} className="text-[#083A6B]" data-testid={`df-anexo-xml-${doc.id}`} />
      ) : (
        <FileCode2 size={13} className="opacity-30" />
      )}
      {doc.pdf_ref ? (
        <Paperclip size={13} className="text-[#083A6B]" data-testid={`df-anexo-pdf-${doc.id}`} />
      ) : (
        <Paperclip size={13} className="opacity-30" />
      )}
    </span>
  );
}

const emptyFilters = () => ({
  de: firstDayOfMonth(),
  ate: today(),
  direcao: "all",
  tipo_documento: "all",
  status: "all",
  fornecedor_id: "",
  fornecedor_nome: "", // apenas para exibição no picker
});

const emptyForm = () => ({
  tipo_documento: "NF-e",
  direcao: "entrada",
  numero: "",
  serie: "",
  chave_acesso: "",
  data_emissao: today(),
  data_competencia: "",
  emitente_nome: "",
  emitente_documento: "",
  destinatario_nome: "",
  destinatario_documento: "",
  valor_documentado: "",
  natureza_operacao: "",
  cfop: "",
  descricao: "",
  fornecedor_id: "",
  fornecedor_nome: "",
  observacoes: "",
  status: "regular",
});

const soDigitos = (s) => (s || "").replace(/\D+/g, "");

export default function DocumentosFiscais() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);

  // Rascunho (o que está sendo editado nos inputs) vs. aplicado (o que foi enviado à API)
  const [rascunho, setRascunho] = useState(emptyFilters());
  const [aplicado, setAplicado] = useState(emptyFilters());
  const [busca, setBusca] = useState(""); // client-side

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // F-FIX.DF.8 — Importar documento fiscal (upload PDF/JPG/PNG + metadados mínimos).
  const [impOpen, setImpOpen] = useState(false);
  const [impSaving, setImpSaving] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impForm, setImpForm] = useState({
    tipo_documento: "NF-e",
    direcao: "entrada",
    numero: "",
    serie: "",
    descricao: "",
  });
  const [me, setMe] = useState(null);

  // F8.4 — Detalhe + Edição + Status
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [novoStatus, setNovoStatus] = useState("regular");
  const [motivoStatus, setMotivoStatus] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);

  // F8.5 — Gestão de vínculos
  const [vincOpen, setVincOpen] = useState(false);
  const [vincFornPicked, setVincFornPicked] = useState({ id: "", nome: "" });
  const [vincPousadaId, setVincPousadaId] = useState("");
  const [vincMotivo, setVincMotivo] = useState("");
  const [savingVinc, setSavingVinc] = useState(false);
  const [pousadasList, setPousadasList] = useState([]);
  const [desvinculandoField, setDesvinculandoField] = useState("");

  // F8.6 — Uploads / Downloads XML e PDF
  const [uploadingKind, setUploadingKind] = useState("");   // "xml" | "pdf" | ""
  const [downloadingKind, setDownloadingKind] = useState("");

  // F8.7 — Auditoria (somente leitura)
  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditErro, setAuditErro] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch { setMe(null); }
    })();
  }, []);

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));

  const load = useCallback(async (filtros) => {
    setLoading(true); setErroCarga(null);
    try {
      const params = {};
      if (filtros.de) params.de = filtros.de;
      if (filtros.ate) params.ate = filtros.ate;
      if (filtros.direcao && filtros.direcao !== "all") params.direcao = filtros.direcao;
      if (filtros.tipo_documento && filtros.tipo_documento !== "all") params.tipo_documento = filtros.tipo_documento;
      if (filtros.status && filtros.status !== "all") params.status = filtros.status;
      if (filtros.fornecedor_id) params.fornecedor_id = filtros.fornecedor_id;
      const { data } = await api.get("/documentos-fiscais", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setErroCarga(
        err?.response?.status === 401
          ? "Sessão expirada. Faça login novamente."
          : "Não foi possível carregar os documentos fiscais.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Primeira carga apenas
  useEffect(() => { load(aplicado); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // F8.7 — Auditoria carrega apenas quando um documento é selecionado
  const carregarAuditoria = useCallback(async (dfId) => {
    if (!dfId) { setAuditItems([]); setAuditErro(""); return; }
    setAuditLoading(true);
    setAuditErro("");
    try {
      const { data } = await api.get("/documentos-fiscais/auditoria", {
        params: { documento_fiscal_id: dfId, limit: 100 },
      });
      setAuditItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const s = err?.response?.status;
      setAuditItems([]);
      if (s === 401) setAuditErro("Sessão expirada.");
      else if (s === 403) setAuditErro("Sem permissão para consultar a auditoria.");
      else setAuditErro("Não foi possível carregar a auditoria.");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (detalheOpen && detalheAlvo?.id) {
      carregarAuditoria(detalheAlvo.id);
    } else if (!detalheOpen) {
      setAuditItems([]);
      setAuditErro("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalheOpen, detalheAlvo?.id]);

  const aplicarFiltros = () => {
    setAplicado(rascunho);
    load(rascunho);
  };

  const limparFiltros = () => {
    const zerado = emptyFilters();
    setRascunho(zerado);
    setAplicado(zerado);
    setBusca("");
    load(zerado);
  };

  const itensFiltradosBusca = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => {
      const alvo = [
        d.numero, d.serie, d.chave_acesso, d.emitente_nome, d.emitente_documento,
        d.destinatario_nome, d.descricao, d.natureza_operacao, d.cfop,
      ].filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [items, busca]);

  const totalValor = useMemo(() => {
    return itensFiltradosBusca.reduce((acc, d) => acc + Number(d.valor_documentado || 0), 0);
  }, [itensFiltradosBusca]);

  const podeLimpar = useMemo(() => {
    const z = emptyFilters();
    return (
      rascunho.de !== z.de || rascunho.ate !== z.ate ||
      rascunho.direcao !== "all" || rascunho.tipo_documento !== "all" ||
      rascunho.status !== "all" || !!rascunho.fornecedor_id || !!busca
    );
  }, [rascunho, busca]);

  const abrirNovo = () => {
    setForm(emptyForm());
    setFormOpen(true);
  };

  // F8.4 — Detalhe
  const abrirDetalhe = async (doc) => {
    setDetalheAlvo(doc);
    setDetalheOpen(true);
    setDetalheLoading(true);
    setEditMode(false);
    try {
      const { data } = await api.get(`/documentos-fiscais/${doc.id}`);
      setDetalheAlvo(data);
    } catch { /* mantém básico */ }
    finally { setDetalheLoading(false); }
  };

  const entrarEdicao = () => {
    if (!detalheAlvo) return;
    setEditForm({
      numero: detalheAlvo.numero || "",
      serie: detalheAlvo.serie || "",
      chave_acesso: detalheAlvo.chave_acesso || "",
      data_emissao: (detalheAlvo.data_emissao || "").slice(0, 10),
      data_competencia: (detalheAlvo.data_competencia || "").slice(0, 10) || "",
      emitente_nome: detalheAlvo.emitente_nome || "",
      emitente_documento: detalheAlvo.emitente_documento || "",
      destinatario_nome: detalheAlvo.destinatario_nome || "",
      destinatario_documento: detalheAlvo.destinatario_documento || "",
      valor_documentado: detalheAlvo.valor_documentado || "",
      natureza_operacao: detalheAlvo.natureza_operacao || "",
      cfop: detalheAlvo.cfop || "",
      descricao: detalheAlvo.descricao || "",
      observacoes: detalheAlvo.observacoes || "",
    });
    setEditMode(true);
  };

  const cancelarEdicao = () => {
    setEditMode(false);
    setEditForm({});
  };

  const salvarEdicao = async (e) => {
    e.preventDefault();
    if (!detalheAlvo) return;
    const valorNum = Number(editForm.valor_documentado);
    if (!valorNum || valorNum <= 0) {
      return toast.error("Valor documentado deve ser maior que zero");
    }
    if (editForm.chave_acesso) {
      const chave = soDigitos(editForm.chave_acesso);
      if (detalheAlvo.tipo_documento === "NF-e" && chave.length !== 44) {
        return toast.error("Chave de acesso NF-e deve ter 44 dígitos");
      }
    }
    const payload = {
      numero: editForm.numero.trim(),
      serie: editForm.serie.trim(),
      chave_acesso: soDigitos(editForm.chave_acesso || ""),
      data_emissao: editForm.data_emissao,
      data_competencia: editForm.data_competencia || null,
      emitente_nome: editForm.emitente_nome.trim(),
      emitente_documento: editForm.emitente_documento.trim(),
      destinatario_nome: editForm.destinatario_nome.trim(),
      destinatario_documento: editForm.destinatario_documento.trim(),
      valor_documentado: valorNum,
      natureza_operacao: editForm.natureza_operacao.trim(),
      cfop: editForm.cfop.trim(),
      descricao: editForm.descricao.trim(),
      observacoes: editForm.observacoes.trim(),
    };
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/documentos-fiscais/${detalheAlvo.id}`, payload);
      toast.success("Documento atualizado");
      setDetalheAlvo(data);
      setEditMode(false);
      load(aplicado);
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (s === 409) toast.error(String(d || "Conflito (chave/numero duplicado)"));
      else if (s === 400) toast.error(String(d || "Dados inválidos"));
      else if (s === 401) toast.error("Sessão expirada");
      else if (s === 403) toast.error("Sem permissão");
      else if (s === 404) toast.error("Documento não encontrado");
      else toast.error("Erro inesperado ao salvar");
    } finally { setSavingEdit(false); }
  };

  const abrirStatus = () => {
    if (!detalheAlvo) return;
    setNovoStatus(detalheAlvo.status || "regular");
    setMotivoStatus(detalheAlvo.motivo_status || "");
    setStatusOpen(true);
  };

  const salvarStatus = async () => {
    if (!detalheAlvo) return;
    const exigeMotivo = ["nao_aplicavel", "cancelado", "divergencia"].includes(novoStatus);
    if (exigeMotivo && !motivoStatus.trim()) {
      return toast.error(`Status "${STATUS_MAP[novoStatus].label}" exige motivo`);
    }
    setChangingStatus(true);
    try {
      const { data } = await api.post(`/documentos-fiscais/${detalheAlvo.id}/status`, {
        status: novoStatus, motivo: motivoStatus.trim(),
      });
      toast.success(novoStatus === "cancelado" ? "Documento cancelado" : "Status atualizado");
      setDetalheAlvo(data);
      setStatusOpen(false);
      load(aplicado);
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (s === 400) toast.error(String(d || "Operação inválida"));
      else if (s === 401) toast.error("Sessão expirada");
      else if (s === 403) toast.error("Sem permissão");
      else if (s === 404) toast.error("Documento não encontrado");
      else toast.error("Erro inesperado");
    } finally { setChangingStatus(false); }
  };

  // F8.5 — Vínculos
  const abrirVinculos = async () => {
    if (!detalheAlvo) return;
    setVincFornPicked({
      id: detalheAlvo.fornecedor_id || "",
      nome: "", // será carregado pelo picker
    });
    setVincPousadaId(detalheAlvo.pousada_id || "");
    setVincMotivo("");
    setVincOpen(true);
    // Carrega pousadas (fonte simples de opções)
    try {
      const { data } = await api.get("/pousadas");
      setPousadasList(Array.isArray(data) ? data : []);
    } catch { setPousadasList([]); }
  };

  const _postVinculos = async (payload) => {
    const { data } = await api.post(
      `/documentos-fiscais/${detalheAlvo.id}/vinculos`, payload
    );
    setDetalheAlvo(data);
    load(aplicado);
    return data;
  };

  const salvarVinculosGerenciados = async () => {
    if (!detalheAlvo) return;
    const payload = { motivo: vincMotivo.trim() };
    // Fornecedor: comparar com estado atual
    const fornAtual = detalheAlvo.fornecedor_id || "";
    const fornNovo = vincFornPicked.id || "";
    if (fornAtual !== fornNovo) payload.fornecedor_id = fornNovo; // "" desvincula, id vincula
    // Pousada
    const pousadaAtual = detalheAlvo.pousada_id || "";
    if (pousadaAtual !== vincPousadaId) payload.pousada_id = vincPousadaId;

    if (Object.keys(payload).length === 1) {
      return toast.info("Nenhuma mudança para salvar");
    }
    setSavingVinc(true);
    try {
      await _postVinculos(payload);
      toast.success("Vínculos atualizados");
      setVincOpen(false);
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (s === 400) toast.error(String(d || "Vínculo inválido"));
      else if (s === 401) toast.error("Sessão expirada");
      else if (s === 403) toast.error("Sem permissão");
      else if (s === 404) toast.error("Documento não encontrado");
      else toast.error("Erro inesperado");
    } finally { setSavingVinc(false); }
  };

  // F8.6 — Upload/Download de anexos fiscais (XML/PDF)
  const _handleUploadError = (err, kind) => {
    const s = err?.response?.status;
    const raw = err?.response?.data?.detail;
    const detail = typeof raw === "string" ? raw : (raw ? JSON.stringify(raw) : "");
    if (s === 400) {
      if (/cancelado/i.test(detail)) {
        toast.error("Documento cancelado — upload bloqueado");
      } else if (/vazio/i.test(detail)) {
        toast.error("Arquivo vazio");
      } else {
        toast.error(detail || "Requisição inválida");
      }
    } else if (s === 401) toast.error("Sessão expirada. Faça login novamente.");
    else if (s === 403) toast.error("Sem permissão para anexar arquivos");
    else if (s === 404) toast.error("Documento não encontrado");
    else if (s === 413) toast.error(`Arquivo maior que ${DF_MAX_UPLOAD_BYTES / 1024 / 1024} MB`);
    else if (s === 415) toast.error(kind === "xml" ? "Arquivo XML inválido" : "Arquivo PDF inválido");
    else if (s === 502 || s === 503) toast.error("Falha no storage — tente novamente");
    else toast.error("Erro inesperado ao enviar arquivo");
  };

  const _handleDownloadError = (err, kind) => {
    const s = err?.response?.status;
    if (s === 404) toast.error(kind === "xml" ? "XML não anexado" : "PDF não anexado");
    else if (s === 401) toast.error("Sessão expirada. Faça login novamente.");
    else if (s === 403) toast.error("Sem permissão para baixar arquivos");
    else if (s === 502 || s === 503) toast.error("Falha no storage — tente novamente");
    else toast.error("Erro inesperado ao baixar arquivo");
  };

  const validarArquivoXml = (file) => {
    if (!file) return "Selecione um arquivo XML";
    if (file.size === 0) return "Arquivo vazio";
    if (file.size > DF_MAX_UPLOAD_BYTES) return `Arquivo maior que ${DF_MAX_UPLOAD_BYTES / 1024 / 1024} MB`;
    const nomeOk = /\.xml$/i.test(file.name || "");
    const mimeOk = DF_XML_MIME_ALLOWED.includes((file.type || "").toLowerCase());
    if (!nomeOk && !mimeOk) return "Selecione um arquivo XML válido (.xml)";
    return null;
  };

  const validarArquivoPdf = (file) => {
    if (!file) return "Selecione um arquivo PDF";
    if (file.size === 0) return "Arquivo vazio";
    if (file.size > DF_MAX_UPLOAD_BYTES) return `Arquivo maior que ${DF_MAX_UPLOAD_BYTES / 1024 / 1024} MB`;
    const nomeOk = /\.pdf$/i.test(file.name || "");
    const mimeOk = DF_PDF_MIME_ALLOWED.includes((file.type || "").toLowerCase());
    if (!nomeOk && !mimeOk) return "Selecione um arquivo PDF válido (.pdf)";
    return null;
  };

  const uploadArquivo = async (kind, file) => {
    if (!detalheAlvo) return;
    if (detalheAlvo.status === "cancelado") {
      toast.error("Documento cancelado não aceita upload");
      return;
    }
    const validador = kind === "xml" ? validarArquivoXml : validarArquivoPdf;
    const erro = validador(file);
    if (erro) { toast.error(erro); return; }

    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post(
        `/documentos-fiscais/${detalheAlvo.id}/${kind}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setDetalheAlvo(data);
      // Atualiza a lista silenciosamente para refletir indicadores
      load(aplicado);
      toast.success(kind === "xml" ? "XML anexado" : "PDF anexado");
    } catch (err) {
      _handleUploadError(err, kind);
    } finally {
      setUploadingKind("");
    }
  };

  const downloadArquivo = async (kind) => {
    if (!detalheAlvo) return;
    const ref = kind === "xml" ? detalheAlvo.xml_ref : detalheAlvo.pdf_ref;
    if (!ref) {
      toast.info(kind === "xml" ? "XML não anexado" : "PDF não anexado");
      return;
    }
    setDownloadingKind(kind);
    try {
      const resp = await api.get(
        `/documentos-fiscais/${detalheAlvo.id}/${kind}`,
        { responseType: "blob" }
      );
      const disp = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"];
      const fallback = kind === "xml"
        ? (detalheAlvo.xml_filename || `documento_${detalheAlvo.id.slice(0, 8)}.xml`)
        : (detalheAlvo.pdf_filename || `documento_${detalheAlvo.id.slice(0, 8)}.pdf`);
      const filename = parseFilenameFromDisposition(disp) || fallback;
      saveBlob(resp.data, filename);
    } catch (err) {
      _handleDownloadError(err, kind);
    } finally {
      setDownloadingKind("");
    }
  };

  const desvincularCampo = async (field, motivo = "") => {
    if (!detalheAlvo) return;
    setDesvinculandoField(field);
    try {
      await _postVinculos({ [field]: "", motivo });
      toast.success("Vínculo removido");
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (s === 400) toast.error(String(d || "Não foi possível remover"));
      else if (s === 404) toast.error("Documento não encontrado");
      else toast.error("Erro inesperado");
    } finally { setDesvinculandoField(""); }
  };

  const fecharForm = () => {
    setFormOpen(false);
    setForm(emptyForm());
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validações client-side amigáveis
    if (!form.tipo_documento || !TIPOS.includes(form.tipo_documento)) {
      return toast.error("Selecione o tipo de documento");
    }
    if (!DIRECOES.find((d) => d.key === form.direcao)) {
      return toast.error("Selecione a direção");
    }
    if (!form.data_emissao) {
      return toast.error("Informe a data de emissão");
    }
    const valorNum = Number(form.valor_documentado);
    if (!valorNum || valorNum <= 0) {
      return toast.error("Valor documentado deve ser maior que zero");
    }
    if (form.chave_acesso) {
      const chave = soDigitos(form.chave_acesso);
      if (form.tipo_documento === "NF-e" && chave.length !== 44) {
        return toast.error("Chave de acesso NF-e deve ter 44 dígitos");
      }
    }
    if (form.data_competencia && form.data_competencia < form.data_emissao) {
      // não bloqueia, mas alerta
    }
    if (!STATUSES.find((s) => s.key === form.status)) {
      return toast.error("Status inválido");
    }

    const payload = {
      tipo_documento: form.tipo_documento,
      direcao: form.direcao,
      numero: form.numero.trim(),
      serie: form.serie.trim(),
      chave_acesso: soDigitos(form.chave_acesso),
      data_emissao: form.data_emissao,
      data_competencia: form.data_competencia || null,
      emitente_nome: form.emitente_nome.trim(),
      emitente_documento: form.emitente_documento.trim(),
      destinatario_nome: form.destinatario_nome.trim(),
      destinatario_documento: form.destinatario_documento.trim(),
      valor_documentado: valorNum,
      natureza_operacao: form.natureza_operacao.trim(),
      cfop: form.cfop.trim(),
      descricao: form.descricao.trim(),
      fornecedor_id: form.fornecedor_id || null,
      observacoes: form.observacoes.trim(),
      status: form.status,
    };

    setSaving(true);
    try {
      await api.post("/documentos-fiscais", payload);
      toast.success("Documento fiscal cadastrado");
      fecharForm();
      // Recarrega mantendo os filtros aplicados
      load(aplicado);
    } catch (err) {
      const s = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (s === 409) {
        toast.error(typeof detail === "string"
          ? detail
          : "Documento fiscal duplicado (chave de acesso ou combinação nº+série+emitente+direção já existente)");
      } else if (s === 400) {
        toast.error(String(detail || "Dados inválidos. Verifique os campos."));
      } else if (s === 401) {
        toast.error("Sessão expirada. Faça login novamente.");
      } else if (s === 403) {
        toast.error("Você não tem permissão para cadastrar documentos fiscais.");
      } else {
        toast.error("Erro inesperado ao cadastrar documento.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="page-documentos-fiscais">
      <PageHeader
        eyebrow="Financeiro · Fiscal"
        title="Documentos Fiscais"
        description="Registro referencial de NF-e, NFS-e, NFC-e e outros documentos. Fonte histórica; não substitui pagamentos ou contas."
        actions={
          podeEscrever ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => { setImpOpen(true); setImpFile(null); }}
                data-testid="btn-df-importar"
              >
                <UploadCloud size={16} /> Importar documento
              </Button>
              <Dialog open={formOpen} onOpenChange={(v) => { if (!v) fecharForm(); else setFormOpen(true); }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-df-novo" onClick={abrirNovo}>
                  <Plus size={16} /> Novo documento
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Novo documento fiscal</DialogTitle>
                  <DialogDescription>
                    Registro referencial. Não altera pagamentos, contas nem reservas. Upload de XML/PDF será liberado em etapa futura.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="df-form-tipo">Tipo de documento *</Label>
                    <Select value={form.tipo_documento}
                      onValueChange={(v) => setForm({ ...form, tipo_documento: v })}>
                      <SelectTrigger id="df-form-tipo" data-testid="select-df-form-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="df-form-direcao">Direção *</Label>
                    <Select value={form.direcao}
                      onValueChange={(v) => setForm({ ...form, direcao: v })}>
                      <SelectTrigger id="df-form-direcao" data-testid="select-df-form-direcao">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIRECOES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="df-form-numero">Número</Label>
                    <Input id="df-form-numero" data-testid="input-df-form-numero"
                      value={form.numero}
                      onChange={(e) => setForm({ ...form, numero: e.target.value })}
                      placeholder="Ex.: 12345" />
                  </div>
                  <div>
                    <Label htmlFor="df-form-serie">Série</Label>
                    <Input id="df-form-serie" data-testid="input-df-form-serie"
                      value={form.serie}
                      onChange={(e) => setForm({ ...form, serie: e.target.value })}
                      placeholder="Ex.: 1" />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="df-form-chave">Chave de acesso {form.tipo_documento === "NF-e" && "(44 dígitos)"}</Label>
                    <Input id="df-form-chave" data-testid="input-df-form-chave"
                      value={form.chave_acesso}
                      onChange={(e) => setForm({ ...form, chave_acesso: e.target.value })}
                      placeholder={form.tipo_documento === "NF-e" ? "0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000" : "Opcional"} />
                    <p className="text-[11px] text-stone-500 mt-1">
                      Único quando presente. NF-e: exatamente 44 dígitos numéricos.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="df-form-data-emissao">Data de emissão *</Label>
                    <Input id="df-form-data-emissao" type="date" data-testid="input-df-form-data-emissao"
                      value={form.data_emissao}
                      onChange={(e) => setForm({ ...form, data_emissao: e.target.value })}
                      required />
                  </div>
                  <div>
                    <Label htmlFor="df-form-data-competencia">Data de competência</Label>
                    <Input id="df-form-data-competencia" type="date" data-testid="input-df-form-data-competencia"
                      value={form.data_competencia}
                      onChange={(e) => setForm({ ...form, data_competencia: e.target.value })} />
                  </div>

                  <div>
                    <Label htmlFor="df-form-emitente-nome">Emitente (nome)</Label>
                    <Input id="df-form-emitente-nome" data-testid="input-df-form-emitente-nome"
                      value={form.emitente_nome}
                      onChange={(e) => setForm({ ...form, emitente_nome: e.target.value })}
                      placeholder="Razão social/nome do emitente" />
                  </div>
                  <div>
                    <Label htmlFor="df-form-emitente-doc">Emitente (CPF/CNPJ)</Label>
                    <Input id="df-form-emitente-doc" data-testid="input-df-form-emitente-doc"
                      value={form.emitente_documento}
                      onChange={(e) => setForm({ ...form, emitente_documento: e.target.value })}
                      placeholder="00.000.000/0000-00" />
                  </div>

                  <div>
                    <Label htmlFor="df-form-dest-nome">Destinatário (nome)</Label>
                    <Input id="df-form-dest-nome" data-testid="input-df-form-dest-nome"
                      value={form.destinatario_nome}
                      onChange={(e) => setForm({ ...form, destinatario_nome: e.target.value })}
                      placeholder="Razão social/nome do destinatário" />
                  </div>
                  <div>
                    <Label htmlFor="df-form-dest-doc">Destinatário (CPF/CNPJ)</Label>
                    <Input id="df-form-dest-doc" data-testid="input-df-form-dest-doc"
                      value={form.destinatario_documento}
                      onChange={(e) => setForm({ ...form, destinatario_documento: e.target.value })}
                      placeholder="000.000.000-00" />
                  </div>

                  <div>
                    <Label htmlFor="df-form-valor">Valor documentado (BRL) *</Label>
                    <Input id="df-form-valor" type="number" step="0.01" min="0.01"
                      data-testid="input-df-form-valor"
                      value={form.valor_documentado}
                      onChange={(e) => setForm({ ...form, valor_documentado: e.target.value })}
                      required />
                  </div>
                  <div>
                    <Label htmlFor="df-form-status">Status inicial</Label>
                    <Select value={form.status}
                      onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger id="df-form-status" data-testid="select-df-form-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.filter((s) => s.key !== "cancelado").map((s) => (
                          <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="df-form-natureza">Natureza da operação</Label>
                    <Input id="df-form-natureza" data-testid="input-df-form-natureza"
                      value={form.natureza_operacao}
                      onChange={(e) => setForm({ ...form, natureza_operacao: e.target.value })}
                      placeholder="Ex.: Venda de mercadoria" />
                  </div>
                  <div>
                    <Label htmlFor="df-form-cfop">CFOP</Label>
                    <Input id="df-form-cfop" data-testid="input-df-form-cfop"
                      value={form.cfop}
                      onChange={(e) => setForm({ ...form, cfop: e.target.value })}
                      placeholder="Ex.: 5102" />
                  </div>

                  <div className="md:col-span-2">
                    <FornecedorPicker
                      value={form.fornecedor_id}
                      valueTexto={form.fornecedor_nome}
                      onSelect={(f) => setForm({
                        ...form,
                        fornecedor_id: f?.id || "",
                        fornecedor_nome: f?.razao_social_nome || "",
                      })}
                      onClear={() => setForm({ ...form, fornecedor_id: "", fornecedor_nome: "" })}
                      onChangeTexto={(txt) => setForm({ ...form, fornecedor_nome: txt })}
                      required={false}
                      pickerTestId="df-form-fornecedor-picker"
                      inputTestId="input-df-form-fornecedor"
                      labelText="Fornecedor vinculado"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="df-form-descricao">Descrição</Label>
                    <Input id="df-form-descricao" data-testid="input-df-form-descricao"
                      value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                      placeholder="Descrição livre" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="df-form-obs">Observações</Label>
                    <Input id="df-form-obs" data-testid="input-df-form-obs"
                      value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                      placeholder="Notas internas (opcional)" />
                  </div>

                  <DialogFooter className="md:col-span-2">
                    <Button type="button" variant="outline" onClick={fecharForm} disabled={saving}
                      data-testid="btn-df-form-cancelar">
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="btn-df-form-salvar">
                      {saving ? "Salvando..." : "Cadastrar documento"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          ) : null
        }
      />

      {/* Filtros */}
      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-stone-500">
          <Filter size={13} /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">De</Label>
            <Input type="date" data-testid="filter-df-de" value={rascunho.de}
              onChange={(e) => setRascunho({ ...rascunho, de: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
            <Input type="date" data-testid="filter-df-ate" value={rascunho.ate}
              onChange={(e) => setRascunho({ ...rascunho, ate: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">Direção</Label>
            <Select value={rascunho.direcao} onValueChange={(v) => setRascunho({ ...rascunho, direcao: v })}>
              <SelectTrigger data-testid="filter-df-direcao"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {DIRECOES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">Tipo</Label>
            <Select value={rascunho.tipo_documento} onValueChange={(v) => setRascunho({ ...rascunho, tipo_documento: v })}>
              <SelectTrigger data-testid="filter-df-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
            <Select value={rascunho.status} onValueChange={(v) => setRascunho({ ...rascunho, status: v })}>
              <SelectTrigger data-testid="filter-df-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <FornecedorPicker
              value={rascunho.fornecedor_id}
              valueTexto={rascunho.fornecedor_nome}
              onSelect={(f) => setRascunho({
                ...rascunho,
                fornecedor_id: f?.id || "",
                fornecedor_nome: f?.razao_social_nome || "",
              })}
              onClear={() => setRascunho({ ...rascunho, fornecedor_id: "", fornecedor_nome: "" })}
              onChangeTexto={(txt) => setRascunho({ ...rascunho, fornecedor_nome: txt })}
              required={false}
              pickerTestId="df-fornecedor-picker"
              inputTestId="input-df-fornecedor"
              labelText="Fornecedor (filtro)"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca textual</Label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input data-testid="input-df-busca" value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Número, chave, emitente, descrição..."
                className="pl-8" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end mt-4">
          <Button variant="outline" onClick={limparFiltros} disabled={!podeLimpar}
                  data-testid="btn-df-limpar-filtros">
            <X size={14} /> Limpar
          </Button>
          <Button onClick={aplicarFiltros} data-testid="btn-df-aplicar-filtros">
            <RefreshCw size={14} /> Aplicar filtros
          </Button>
        </div>
      </div>

      {/* Sumário */}
      {!loading && !erroCarga && itensFiltradosBusca.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Documentos exibidos</div>
            <div className="font-serif text-2xl text-stone-900 tabular-nums" data-testid="df-summary-count">
              {itensFiltradosBusca.length}
            </div>
          </div>
          <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Valor documentado (soma)</div>
            <div className="font-serif text-2xl text-stone-900 tabular-nums" data-testid="df-summary-total">
              {brl(totalValor)}
            </div>
          </div>
          <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Período</div>
            <div className="text-sm text-stone-700">
              {fmtDataBR(aplicado.de)} → {fmtDataBR(aplicado.ate)}
            </div>
          </div>
        </div>
      )}

      {/* Estados */}
      {loading ? (
        <div data-testid="df-loading"
             className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando documentos fiscais...
        </div>
      ) : erroCarga ? (
        <div data-testid="df-erro"
             className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erroCarga}
        </div>
      ) : itensFiltradosBusca.length === 0 ? (
        <div data-testid="df-empty"
             className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <FileText size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">
            Nenhum documento fiscal encontrado.
          </div>
          <p className="text-sm text-stone-600 mb-2 max-w-md mx-auto">
            Ajuste os filtros ou clique em <b>Novo documento</b> para cadastrar.
          </p>
          <p className="text-xs text-stone-500 max-w-md mx-auto">
            Você pode registrar NF-e/NFS-e/NFC-e, alterar status, gerenciar vínculos e anexar XML/PDF diretamente pelo detalhe.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {itensFiltradosBusca.map((d) => (
              <div key={d.id} data-testid={`df-card-${d.id}`}
                   className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-widest text-stone-500">
                        {d.tipo_documento || "Documento"}
                      </span>
                      <DirecaoTag direcao={d.direcao} />
                    </div>
                    <div className="font-serif text-lg text-stone-900 truncate">
                      {d.numero ? `Nº ${d.numero}` : (d.chave_acesso ? `Chave ${d.chave_acesso.slice(-8)}` : "Sem número")}
                    </div>
                    {d.serie && <div className="text-[11px] text-stone-500">Série {d.serie}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={d.status} />
                    <AnexosIcons doc={d} />
                  </div>
                </div>
                <div className="text-xs text-stone-600 space-y-1 mb-3">
                  <div><b>Emissão:</b> {fmtDataBR(d.data_emissao)}</div>
                  {d.emitente_nome && <div className="truncate"><b>Emitente:</b> {d.emitente_nome}</div>}
                  <div><b>Valor:</b> <span className="tabular-nums">{brl(Number(d.valor_documentado || 0))}</span></div>
                  {d.fornecedor_id && (
                    <div className="text-[10px] text-[#083A6B]">↳ Fornecedor vinculado</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(d)}
                          data-testid={`btn-df-detalhe-${d.id}`}>
                    Detalhes
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nº / Série</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Direção</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Emissão</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Emitente</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Valor</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Vínculo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-center">Anexos</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensFiltradosBusca.map((d) => (
                  <TableRow key={d.id} data-testid={`df-row-${d.id}`}>
                    <TableCell className="text-sm text-stone-900">
                      <div className="font-medium">{d.numero || "—"}</div>
                      {d.serie && <div className="text-[11px] text-stone-500">Série {d.serie}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-stone-700">{d.tipo_documento || "—"}</TableCell>
                    <TableCell><DirecaoTag direcao={d.direcao} /></TableCell>
                    <TableCell className="text-sm text-stone-700 tabular-nums">
                      {fmtDataBR(d.data_emissao)}
                    </TableCell>
                    <TableCell className="text-sm text-stone-700 max-w-[220px] truncate">
                      {d.emitente_nome || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-stone-900 text-right tabular-nums">
                      {brl(Number(d.valor_documentado || 0))}
                    </TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-sm text-stone-600">
                      {d.fornecedor_id ? (
                        <span className="text-[11px] text-[#083A6B] font-semibold uppercase tracking-widest">
                          Fornecedor
                        </span>
                      ) : (
                        <span className="text-[11px] text-stone-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center"><AnexosIcons doc={d} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(d)}
                        data-testid={`btn-df-detalhe-${d.id}`}
                        aria-label={`Detalhes de ${d.numero || d.id}`}>
                        <MoreHorizontal size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* F8.4 — Dialog Detalhe (com toggle Edição) */}
      <Dialog open={detalheOpen} onOpenChange={(v) => {
        setDetalheOpen(v);
        if (!v) { setDetalheAlvo(null); setEditMode(false); setEditForm({}); }
      }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-df-detalhe">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} className="text-[#083A6B]" />
              {detalheAlvo ? `${detalheAlvo.tipo_documento || "Documento"} ${detalheAlvo.numero || ""}` : "Detalhes"}
            </DialogTitle>
            <DialogDescription>
              {editMode ? "Modo edição — apenas campos permitidos pelo backend." : "Registro referencial. Auditoria em etapa futura."}
            </DialogDescription>
          </DialogHeader>

          {detalheLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando...</div>
          ) : !detalheAlvo ? null : editMode ? (
            <form onSubmit={salvarEdicao} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Número</Label>
                <Input value={editForm.numero} data-testid="input-df-edit-numero"
                  onChange={(e) => setEditForm({ ...editForm, numero: e.target.value })} />
              </div>
              <div>
                <Label>Série</Label>
                <Input value={editForm.serie} data-testid="input-df-edit-serie"
                  onChange={(e) => setEditForm({ ...editForm, serie: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Chave de acesso</Label>
                <Input value={editForm.chave_acesso} data-testid="input-df-edit-chave"
                  onChange={(e) => setEditForm({ ...editForm, chave_acesso: e.target.value })} />
              </div>
              <div>
                <Label>Data emissão</Label>
                <Input type="date" value={editForm.data_emissao} data-testid="input-df-edit-data-emissao"
                  onChange={(e) => setEditForm({ ...editForm, data_emissao: e.target.value })} />
              </div>
              <div>
                <Label>Data competência</Label>
                <Input type="date" value={editForm.data_competencia || ""} data-testid="input-df-edit-data-competencia"
                  onChange={(e) => setEditForm({ ...editForm, data_competencia: e.target.value })} />
              </div>
              <div>
                <Label>Emitente (nome)</Label>
                <Input value={editForm.emitente_nome} data-testid="input-df-edit-emitente-nome"
                  onChange={(e) => setEditForm({ ...editForm, emitente_nome: e.target.value })} />
              </div>
              <div>
                <Label>Emitente (documento)</Label>
                <Input value={editForm.emitente_documento} data-testid="input-df-edit-emitente-doc"
                  onChange={(e) => setEditForm({ ...editForm, emitente_documento: e.target.value })} />
              </div>
              <div>
                <Label>Destinatário (nome)</Label>
                <Input value={editForm.destinatario_nome} data-testid="input-df-edit-dest-nome"
                  onChange={(e) => setEditForm({ ...editForm, destinatario_nome: e.target.value })} />
              </div>
              <div>
                <Label>Destinatário (documento)</Label>
                <Input value={editForm.destinatario_documento} data-testid="input-df-edit-dest-doc"
                  onChange={(e) => setEditForm({ ...editForm, destinatario_documento: e.target.value })} />
              </div>
              <div>
                <Label>Valor documentado (BRL)</Label>
                <Input type="number" step="0.01" min="0.01" value={editForm.valor_documentado}
                  data-testid="input-df-edit-valor"
                  onChange={(e) => setEditForm({ ...editForm, valor_documentado: e.target.value })} />
              </div>
              <div>
                <Label>CFOP</Label>
                <Input value={editForm.cfop} data-testid="input-df-edit-cfop"
                  onChange={(e) => setEditForm({ ...editForm, cfop: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Natureza da operação</Label>
                <Input value={editForm.natureza_operacao} data-testid="input-df-edit-natureza"
                  onChange={(e) => setEditForm({ ...editForm, natureza_operacao: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Descrição</Label>
                <Input value={editForm.descricao} data-testid="input-df-edit-descricao"
                  onChange={(e) => setEditForm({ ...editForm, descricao: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Observações</Label>
                <Input value={editForm.observacoes} data-testid="input-df-edit-obs"
                  onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })} />
              </div>
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={cancelarEdicao} disabled={savingEdit}
                        data-testid="btn-df-edit-cancelar">
                  Cancelar edição
                </Button>
                <Button type="submit" disabled={savingEdit} data-testid="btn-df-edit-salvar">
                  {savingEdit ? "Salvando..." : "Salvar alterações"}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><div className="text-[11px] uppercase text-stone-500">Tipo</div>{detalheAlvo.tipo_documento || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Direção</div><DirecaoTag direcao={detalheAlvo.direcao} /></div>
                <div><div className="text-[11px] uppercase text-stone-500">Número</div>{detalheAlvo.numero || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Série</div>{detalheAlvo.serie || "—"}</div>
                <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Chave de acesso</div><span className="font-mono text-xs">{detalheAlvo.chave_acesso || "—"}</span></div>
                <div><div className="text-[11px] uppercase text-stone-500">Data emissão</div>{fmtDataBR(detalheAlvo.data_emissao)}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Data competência</div>{fmtDataBR(detalheAlvo.data_competencia)}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Emitente</div>{detalheAlvo.emitente_nome || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Doc. emitente</div>{detalheAlvo.emitente_documento || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Destinatário</div>{detalheAlvo.destinatario_nome || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Doc. destinatário</div>{detalheAlvo.destinatario_documento || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Valor documentado</div><span className="tabular-nums">{brl(Number(detalheAlvo.valor_documentado || 0))}</span></div>
                <div><div className="text-[11px] uppercase text-stone-500">CFOP</div>{detalheAlvo.cfop || "—"}</div>
                <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Natureza da operação</div>{detalheAlvo.natureza_operacao || "—"}</div>
                <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Descrição</div>{detalheAlvo.descricao || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Status</div><StatusBadge status={detalheAlvo.status} /></div>
                <div><div className="text-[11px] uppercase text-stone-500">Motivo status</div>{detalheAlvo.motivo_status || "—"}</div>
                <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Observações</div>{detalheAlvo.observacoes || "—"}</div>
              </div>

              <div className="border-t border-stone-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-stone-500">Vínculos</div>
                  {podeEscrever && detalheAlvo.status !== "cancelado" && (
                    <Button size="sm" variant="outline" onClick={abrirVinculos}
                            data-testid="btn-df-gerenciar-vinculos">
                      Gerenciar vínculos
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-stone-700">
                  {[
                    ["fornecedor_id", "Fornecedor"],
                    ["pousada_id", "Pousada"],
                    ["empresa_id", "Empresa"],
                    ["hospede_id", "Hóspede"],
                    ["reserva_id", "Reserva"],
                    ["conta_pagar_id", "Conta a Pagar"],
                    ["conta_receber_id", "Conta a Receber"],
                    ["movimento_financeiro_id", "Movimento"],
                    ["pagamento_id", "Pagamento"],
                  ].map(([field, label]) => (
                    <div key={field} className="flex items-center justify-between border border-stone-200 rounded px-2 py-1.5 bg-white"
                         data-testid={`df-vinculo-${field}`}>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
                        <div className="text-xs text-stone-800 font-mono truncate">
                          {detalheAlvo[field] ? `${detalheAlvo[field].slice(0, 8)}…` : "—"}
                        </div>
                      </div>
                      {detalheAlvo[field] && podeEscrever && detalheAlvo.status !== "cancelado" && (
                        <Button size="sm" variant="ghost"
                                onClick={() => desvincularCampo(field)}
                                disabled={desvinculandoField === field}
                                data-testid={`btn-df-desvincular-${field}`}
                                aria-label={`Remover vínculo ${label}`}>
                          <X size={12} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-stone-500 mt-2">
                  Fornecedor e Pousada podem ser editados por seletor. Demais vínculos podem apenas ser <b>removidos</b> por aqui —
                  novos vínculos com esses são criados a partir do respectivo módulo (CP, CR, Reserva, etc.) ou via API.
                </p>
              </div>

              {/* F8.6 — Anexos fiscais (XML/PDF) */}
              <div className="border-t border-stone-200 pt-3" data-testid="df-anexos-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-stone-500">Anexos fiscais</div>
                  <div className="text-[10px] text-stone-500">Limite: 15 MB por arquivo</div>
                </div>
                {detalheAlvo.status === "cancelado" && (
                  <div className="mb-3 text-[11px] rounded border border-stone-300 bg-stone-50 px-2 py-1.5 text-stone-600"
                       data-testid="df-anexos-cancelado-alert">
                    Documento cancelado — arquivos existentes podem ser baixados, mas novos anexos não são permitidos.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    {
                      kind: "xml",
                      label: "XML",
                      accept: ".xml,application/xml,text/xml",
                      ref: detalheAlvo.xml_ref,
                      filename: detalheAlvo.xml_filename,
                      size: detalheAlvo.xml_size,
                      uploaded_at: detalheAlvo.xml_uploaded_at,
                      Icon: FileCode2,
                    },
                    {
                      kind: "pdf",
                      label: "PDF",
                      accept: ".pdf,application/pdf",
                      ref: detalheAlvo.pdf_ref,
                      filename: detalheAlvo.pdf_filename,
                      size: detalheAlvo.pdf_size,
                      uploaded_at: detalheAlvo.pdf_uploaded_at,
                      Icon: Paperclip,
                    },
                  ].map(({ kind, label, accept, ref, filename, size, uploaded_at, Icon }) => {
                    const anexado = !!ref;
                    const inputId = `df-anexo-${kind}-input`;
                    const uploading = uploadingKind === kind;
                    const downloading = downloadingKind === kind;
                    const uploadDisabled =
                      !podeEscrever ||
                      detalheAlvo.status === "cancelado" ||
                      uploading;
                    return (
                      <div key={kind}
                           className="border border-stone-200 rounded p-3 bg-white flex flex-col gap-2"
                           data-testid={`df-anexo-${kind}-card`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon size={14} className="text-[#083A6B]" />
                            <span className="text-xs font-semibold uppercase tracking-widest text-stone-700">{label}</span>
                          </div>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                              anexado
                                ? "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20"
                                : "bg-stone-100 text-stone-500 border-stone-300"
                            }`}
                            data-testid={`df-anexo-${kind}-status`}
                          >
                            {anexado ? "Anexado" : "Não anexado"}
                          </span>
                        </div>

                        <div className="text-xs text-stone-700 space-y-0.5 min-h-[3rem]">
                          {anexado ? (
                            <>
                              <div className="flex gap-1">
                                <span className="text-stone-500 shrink-0">Nome:</span>
                                <span className="truncate" data-testid={`df-anexo-${kind}-nome`} title={filename || ""}>
                                  {filename || "—"}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                <span className="text-stone-500 shrink-0">Tamanho:</span>
                                <span data-testid={`df-anexo-${kind}-size`}>{fmtBytes(size)}</span>
                              </div>
                              <div className="flex gap-1">
                                <span className="text-stone-500 shrink-0">Upload:</span>
                                <span data-testid={`df-anexo-${kind}-data`}>{fmtDataHoraBR(uploaded_at)}</span>
                              </div>
                            </>
                          ) : (
                            <div className="text-stone-500 italic">Nenhum arquivo anexado.</div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2 pt-1">
                          <input
                            id={inputId}
                            type="file"
                            accept={accept}
                            className="hidden"
                            data-testid={`input-df-anexo-${kind}`}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = ""; // permite re-enviar o mesmo arquivo
                              if (f) uploadArquivo(kind, f);
                            }}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={uploadDisabled}
                            onClick={() => document.getElementById(inputId)?.click()}
                            data-testid={`btn-df-anexo-${kind}-upload`}
                            aria-label={`Enviar ${label}`}
                          >
                            {uploading
                              ? "Enviando..."
                              : anexado ? `Substituir ${label}` : `Enviar ${label}`}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!anexado || downloading}
                            onClick={() => downloadArquivo(kind)}
                            data-testid={`btn-df-anexo-${kind}-download`}
                            aria-label={`Baixar ${label}`}
                          >
                            {downloading ? "Baixando..." : "Baixar"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-stone-500 mt-2">
                  Formatos aceitos: XML (application/xml, text/xml) e PDF (application/pdf). Documentos cancelados não aceitam novos anexos.
                </p>
              </div>

              {/* F8.7 — Auditoria (somente leitura) */}
              <div className="border-t border-stone-200 pt-3" data-testid="df-auditoria-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-stone-500">Auditoria</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-stone-500">Somente leitura</span>
                    <Button size="sm" variant="ghost" onClick={() => carregarAuditoria(detalheAlvo.id)}
                            disabled={auditLoading}
                            data-testid="btn-df-auditoria-retry"
                            aria-label="Recarregar auditoria">
                      <RefreshCw size={12} />
                    </Button>
                  </div>
                </div>

                {auditLoading ? (
                  <div className="text-xs text-stone-500 py-2" data-testid="df-auditoria-loading">
                    Carregando histórico...
                  </div>
                ) : auditErro ? (
                  <div className="text-xs rounded border border-[#D4A652]/30 bg-[#F7E4DE] text-[#8A3A22] px-2 py-2 flex items-center justify-between"
                       data-testid="df-auditoria-erro">
                    <span>{auditErro}</span>
                    <Button size="sm" variant="outline"
                            onClick={() => carregarAuditoria(detalheAlvo.id)}
                            data-testid="btn-df-auditoria-retry-inline">
                      Tentar novamente
                    </Button>
                  </div>
                ) : auditItems.length === 0 ? (
                  <div className="text-xs text-stone-500 italic py-2" data-testid="df-auditoria-vazio">
                    Nenhum evento de auditoria encontrado.
                  </div>
                ) : (
                  <ol className="space-y-2" data-testid="df-auditoria-lista">
                    {auditItems.map((ev, idx) => {
                      const meta = labelAcaoDF(ev.acao);
                      const resumo = resumoEventoDF(ev);
                      const temDetalhes = !!(ev.extras && Object.keys(ev.extras).length) ||
                                          !!(ev.before && Object.keys(ev.before || {}).length) ||
                                          !!(ev.after && Object.keys(ev.after || {}).length);
                      return (
                        <li key={ev.id || idx}
                            className="border border-stone-200 rounded p-2 bg-white"
                            data-testid={`df-auditoria-item-${idx}`}>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${meta.cls}`}
                              data-testid={`df-auditoria-acao-${idx}`}
                            >
                              {meta.label}
                            </span>
                            <span className="text-[11px] text-stone-500 tabular-nums"
                                  data-testid={`df-auditoria-quando-${idx}`}>
                              {fmtDataHoraBR(ev.quando)}
                            </span>
                            <span className="text-[11px] text-stone-500 truncate"
                                  title={ev.por || ""}
                                  data-testid={`df-auditoria-por-${idx}`}>
                              · {ev.por || "—"}
                            </span>
                          </div>
                          {resumo && (
                            <div className="text-xs text-stone-800 break-words"
                                 data-testid={`df-auditoria-resumo-${idx}`}>
                              {resumo}
                            </div>
                          )}
                          {temDetalhes && (
                            <details className="mt-1"
                                     data-testid={`df-auditoria-detalhes-${idx}`}>
                              <summary className="text-[10px] text-stone-500 cursor-pointer select-none">
                                Ver detalhes técnicos
                              </summary>
                              <pre className="mt-1 text-[10px] leading-tight bg-stone-50 border border-stone-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
{JSON.stringify(
  {
    ...(ev.extras && Object.keys(ev.extras).length ? { extras: ev.extras } : {}),
    ...(ev.before ? { before: ev.before } : {}),
    ...(ev.after ? { after: ev.after } : {}),
  },
  null,
  2,
)}
                              </pre>
                            </details>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
                <p className="text-[10px] text-stone-500 mt-2">
                  Histórico append-only mantido pelo backend. Auditoria é somente leitura — nenhum evento pode ser editado ou removido pela UI.
                </p>
              </div>

              <div className="text-[11px] text-stone-500 border-t border-stone-200 pt-3">
                Criado {detalheAlvo.created_at?.slice(0, 10) || "—"} · Atualizado {detalheAlvo.updated_at?.slice(0, 10) || "—"}
                {detalheAlvo.created_by && <> · por {detalheAlvo.created_by}</>}
              </div>

              {podeEscrever && (
                <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-stone-200">
                  <Button variant="outline" onClick={abrirStatus}
                          disabled={detalheAlvo.status === "cancelado"}
                          data-testid="btn-df-alterar-status">
                    Alterar status
                  </Button>
                  <Button onClick={entrarEdicao}
                          disabled={detalheAlvo.status === "cancelado"}
                          data-testid="btn-df-editar">
                    Editar
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* F8.4 — Dialog Alterar Status */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-df-status">
          <DialogHeader>
            <DialogTitle>Alterar status do documento</DialogTitle>
            <DialogDescription>
              O cancelamento é definitivo e bloqueia futuras edições/vínculos/uploads.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Novo status</Label>
              <Select value={novoStatus} onValueChange={setNovoStatus}>
                <SelectTrigger data-testid="select-df-novo-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo {["nao_aplicavel", "cancelado", "divergencia"].includes(novoStatus) && "(obrigatório)"}</Label>
              <Input value={motivoStatus} data-testid="input-df-motivo-status"
                     onChange={(e) => setMotivoStatus(e.target.value)}
                     placeholder={["nao_aplicavel", "cancelado", "divergencia"].includes(novoStatus) ? "Descreva o motivo" : "Opcional"} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)} disabled={changingStatus}>
              Cancelar
            </Button>
            <Button onClick={salvarStatus} disabled={changingStatus} data-testid="btn-df-salvar-status">
              {changingStatus ? "Aplicando..." : (novoStatus === "cancelado" ? "Cancelar documento" : "Aplicar status")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* F8.5 — Dialog Gerenciar Vínculos */}
      <Dialog open={vincOpen} onOpenChange={setVincOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="dialog-df-vinculos">
          <DialogHeader>
            <DialogTitle>Gerenciar vínculos</DialogTitle>
            <DialogDescription>
              Fornecedor e Pousada podem ser vinculados/substituídos aqui. Vínculos com Reserva/CP/CR/Movimento/Pagamento são criados nos respectivos módulos; aqui só é possível removê-los.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <FornecedorPicker
              value={vincFornPicked.id}
              valueTexto={vincFornPicked.nome}
              onSelect={(f) => setVincFornPicked({ id: f?.id || "", nome: f?.razao_social_nome || "" })}
              onClear={() => setVincFornPicked({ id: "", nome: "" })}
              onChangeTexto={(txt) => setVincFornPicked((prev) => ({ ...prev, nome: txt }))}
              required={false}
              pickerTestId="df-vinc-fornecedor-picker"
              inputTestId="input-df-vinc-fornecedor"
              labelText="Fornecedor"
            />
            <div>
              <Label>Pousada</Label>
              <Select value={vincPousadaId || "none"}
                      onValueChange={(v) => setVincPousadaId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="select-df-vinc-pousada"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— (nenhuma)</SelectItem>
                  {pousadasList.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nome || p.id.slice(0, 8)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo (opcional)</Label>
              <Input value={vincMotivo} data-testid="input-df-vinc-motivo"
                onChange={(e) => setVincMotivo(e.target.value)}
                placeholder="Ex.: correção de vínculo com fornecedor" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVincOpen(false)} disabled={savingVinc}>Cancelar</Button>
            <Button onClick={salvarVinculosGerenciados} disabled={savingVinc}
                    data-testid="btn-df-vinc-salvar">
              {savingVinc ? "Salvando..." : "Salvar vínculos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F-FIX.DF.8 — Importar documento fiscal: upload direto + metadados mínimos. */}
      <Dialog open={impOpen} onOpenChange={(v) => {
        setImpOpen(v);
        if (!v) { setImpFile(null); setImpSaving(false); }
      }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dialog-df-importar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UploadCloud size={18} className="text-[#083A6B]" /> Importar documento fiscal
            </DialogTitle>
            <DialogDescription>
              Envie um PDF, JPG ou PNG. O arquivo fica anexado ao registro para consulta futura.
              Esta importação <b>não</b> altera pagamentos nem lança movimentações.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <label
              htmlFor="df-imp-file"
              className="block border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-[#083A6B] hover:bg-[#F5F1E8] cursor-pointer transition-colors"
            >
              <UploadCloud size={24} className="mx-auto text-stone-400 mb-2" />
              <div className="text-sm font-medium text-stone-700">
                {impFile ? impFile.name : "Clique para escolher o arquivo"}
              </div>
              <div className="text-xs text-stone-500 mt-1">PDF, JPG ou PNG</div>
              <input
                id="df-imp-file"
                data-testid="input-df-imp-file"
                type="file"
                accept="application/pdf,image/jpeg,image/jpg,image/png"
                className="hidden"
                onChange={(e) => setImpFile(e.target.files?.[0] || null)}
              />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="df-imp-tipo">Tipo de documento *</Label>
                <Select
                  value={impForm.tipo_documento}
                  onValueChange={(v) => setImpForm({ ...impForm, tipo_documento: v })}
                >
                  <SelectTrigger id="df-imp-tipo" data-testid="select-df-imp-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="df-imp-direcao">Direção *</Label>
                <Select
                  value={impForm.direcao}
                  onValueChange={(v) => setImpForm({ ...impForm, direcao: v })}
                >
                  <SelectTrigger id="df-imp-direcao" data-testid="select-df-imp-direcao"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIRECOES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="df-imp-numero">Número</Label>
                <Input id="df-imp-numero" data-testid="input-df-imp-numero"
                  value={impForm.numero}
                  onChange={(e) => setImpForm({ ...impForm, numero: e.target.value })}
                  placeholder="Ex.: 12345" />
              </div>
              <div>
                <Label htmlFor="df-imp-serie">Série</Label>
                <Input id="df-imp-serie" data-testid="input-df-imp-serie"
                  value={impForm.serie}
                  onChange={(e) => setImpForm({ ...impForm, serie: e.target.value })}
                  placeholder="Ex.: 1" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="df-imp-desc">Descrição</Label>
                <Input id="df-imp-desc" data-testid="input-df-imp-descricao"
                  value={impForm.descricao}
                  onChange={(e) => setImpForm({ ...impForm, descricao: e.target.value })}
                  placeholder="Notas internas (opcional)" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpOpen(false)} disabled={impSaving} data-testid="btn-df-imp-cancelar">
              Cancelar
            </Button>
            <Button
              disabled={!impFile || impSaving}
              onClick={async () => {
                if (!impFile) return toast.error("Escolha um arquivo");
                setImpSaving(true);
                const fd = new FormData();
                fd.append("file", impFile);
                fd.append("tipo_documento", impForm.tipo_documento);
                fd.append("direcao", impForm.direcao);
                fd.append("numero", impForm.numero || "");
                fd.append("serie", impForm.serie || "");
                fd.append("descricao", impForm.descricao || "");
                try {
                  await api.post("/documentos-fiscais/importar", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  toast.success("Documento fiscal importado");
                  setImpOpen(false);
                  setImpFile(null);
                  aplicarFiltros();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Erro ao importar documento");
                } finally {
                  setImpSaving(false);
                }
              }}
              data-testid="btn-df-imp-confirmar"
            >
              {impSaving ? "Importando..." : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
